import { read, TABLE } from '../data/db';
import {
  hypotheses,
  matchNotes,
  matches,
  observations,
  profile,
  talkPoints,
  teams,
} from '../data/repos';
import { deriveMatchMinute } from '../domain/clock';
import { toHypothesisRow } from '../domain/setup';
import type { PreMatchCandidate } from '../domain/setup';
import { firstIncrements } from './discovery';
import type {
  AddHypothesisAtHalftimeCommand,
  PromoteHalftimeAdjustmentCommand,
  SwapBoardTileCommand,
  WriteMatchNoteCommand,
} from '../domain/commands';
import type { AdjustmentCandidate } from '../domain/halftime';
import { ARTIFACT_PHASE } from '../types';
import type {
  HalftimeTalkPointRow,
  MatchNoteRow,
  MatchRow,
  Status,
  StatusOverride,
  TalkPointRow,
  TalkPointSelection,
  TemplateRow,
} from '../types';
import { dispatch } from './bus';
import type { DispatchResult } from './bus';
import { newId } from './ids';
import { recorder } from './recorder';
import type { RecorderAction } from './recorder';
import type { UndoStripRow } from './setPiece';

/**
 * HT-1 … HT-8 — the **I/O half** of the halftime hub (build unit U-13a).
 *
 * §1.2 forbids `ui/` from importing `data/`, so everything the hub renders arrives through here and
 * every write leaves through `dispatch`. The *decisions* are elsewhere: `domain/halftime.ts` owns
 * HT-5's eligibility, ordering and §7.3's snapshot rule; `domain/commands/halftime.ts` owns the
 * guards; `domain/undo.ts` owns the strip's target. This module reads, mints ids, derives SES-10's
 * minute and dispatches.
 */

export interface HalftimeContext {
  readonly match: MatchRow;
  /** §5.6's top zone shows *"clock · opponent · half"*, and the opponent is a name, not an id. */
  readonly opponent: string;
  /** HT-5's input, unfiltered — `snapshotAdjustments` needs the ineligible rows too (§7.3). */
  readonly candidates: readonly AdjustmentCandidate[];
  /**
   * The halftime selection already promoted, in rank order.
   *
   * Narrowed to the **halftime arm** of `TalkPointRow` rather than left as the union. INV-13's
   * discrimination is the difference — `instructionText` is `string | null` at halftime and `string`
   * at full time — so a caller rebuilding a selection from the union produces a value assignable to
   * neither arm. Narrowing at the read is the one place the phase is actually known.
   */
  readonly promoted: readonly HalftimeTalkPointRow[];
  /** HT-8 — the note for this match's break, if one has been started. */
  readonly note: MatchNoteRow | undefined;
  /** §5.6 — the undo strip's candidates. Not the hub's content; the chrome's. */
  readonly undoRows: readonly UndoStripRow[];
  /**
   * §7.4 / HT-3 — whether the board is still unlocked, which decides whether the *added* "Lock my 8"
   * card renders. **Added, never substituted** — see `halftimeRoutes`.
   */
  readonly stillInDiscovery: boolean;
  /**
   * HT-2's list (build unit U-13b) — *"hypotheses sorted by count with status"*.
   *
   * A **second projection of the same rows**, not a widening of `candidates`, and the difference is
   * `boardPosition`. `AdjustmentCandidate` omits that field on purpose — §6.3's argument is that a
   * comparator whose input type cannot see a value cannot rank on it, and HT-5's order must never
   * acquire a board term. HT-2 is the surface where position *is* the subject (LOCK-6's swap needs
   * to know which tiles are on the board and which slot each holds), so it gets its own type rather
   * than HT-5's growing a field it must not use.
   */
  readonly manageRows: readonly ManageRow[];
  /**
   * PRE-2's seeded library, for the add panel (PRE-3 / PRE-4).
   *
   * Read in the **same** transaction as everything else rather than fetched by the sheet on open.
   * §7.1 requires opening a card to move nothing on the hub, and a sheet that fetched on mount would
   * render an empty library for a frame — on a surface the user reaches with a few minutes and a team
   * in front of them, a list that appears late reads as a list that is not there.
   */
  readonly library: readonly TemplateRow[];
}

/**
 * One row of HT-2's *Manage patterns* list.
 *
 * `cachedCount` is here and `boardPosition` is here, which is exactly the pair `AdjustmentCandidate`
 * refuses to hold together — HT-2 sorts by count *and* renders board membership, and it is not a
 * comparator input for anything else.
 */
export interface ManageRow {
  readonly id: string;
  readonly text: string;
  readonly statusOverride: StatusOverride;
  readonly status: Status;
  readonly cachedCount: number;
  /** `null` = list-only. LOCK-6's swap turns one of these into a slot and another back into `null`. */
  readonly boardPosition: number | null;
}

/**
 * Everything the hub needs, from **one** readonly transaction.
 *
 * One transaction because the HT-5 candidate list and the promoted selection describe the same
 * moment: a candidate list read before a promote and a selection read after it would render a hub
 * whose ticks disagree with its list, which on a screen the user reads aloud from is the one error
 * that reaches the touchline.
 *
 * @returns `undefined` when the match has vanished — a normal outcome after a cascade.
 * @throws {StorageError} on a read failure. Not swallowed: an empty hub and a failed read look
 *   identical, and only one of them is worth trying again.
 */
export async function loadHalftimeContext(matchId: string): Promise<HalftimeContext | undefined> {
  return read(
    [
      TABLE.matches,
      TABLE.teams,
      TABLE.hypotheses,
      TABLE.observations,
      TABLE.talkPoints,
      TABLE.matchNotes,
      TABLE.templates,
    ],
    async (): Promise<HalftimeContext | undefined> => {
      const match = await matches.getMatch(matchId);
      if (match === undefined) return undefined;

      const rows = await hypotheses.listHypothesesForMatch(matchId);
      const notes = await matchNotes.listForMatch(matchId, ARTIFACT_PHASE.halftime);
      // §6.1's second sort term, from **the same helper Discovery uses**. HT-5's chain is §6.1's
      // chain, and a second derivation of "earliest surviving increment" here would be a second
      // chance for the two orders to disagree — the same argument `order.ts` makes for exporting
      // `compareDiscovery` rather than restating it in `lock.ts`.
      const firstIncrementByHypothesis = firstIncrements(
        await observations.listObservationsForMatch(matchId),
      );

      return {
        match,
        opponent: (await teams.getTeam(match.teamId))?.name ?? 'Opponent',
        // Projected to exactly what `AdjustmentCandidate` declares. Handing whole rows would let a
        // later edit rank on `boardPosition` or `lastIncrementTs` without anything objecting.
        candidates: rows.map((row) => ({
          id: row.id,
          text: row.text,
          statusOverride: row.statusOverride,
          status: row.status,
          cachedCount: row.cachedCount,
          firstObservationCreatedAt: firstIncrementByHypothesis.get(row.id) ?? null,
          createdAt: row.createdAt,
        })),
        promoted: (await talkPoints.listForMatch(matchId, ARTIFACT_PHASE.halftime)).filter(
          isHalftimeTalkPoint,
        ),
        // HT-8 keeps **one** note per break. `listForMatch` sorts by `createdAt`, so the first is the
        // one the user started; taking the newest instead would strand the earlier one invisibly.
        note: notes[0],
        undoRows: rows.map((row) => ({
          id: row.id,
          text: row.text,
          lastIncrementTs: row.lastIncrementTs,
        })),
        stillInDiscovery: match.capturePhase === 'discovery',
        // HT-2 — *"sorted by count with status"*. Sorted at the read rather than in the sheet so the
        // order is a property of the data: two renders of the same break must not disagree about
        // which pattern is top of the list the user is choosing board composition from.
        //
        // The tiebreak is `createdAt asc, id asc` — §3.2's `v002` column and §6.1's last two terms.
        // Ties are the common case here, not the edge: every pattern with count 0 ties on term one,
        // and without a total order the list re-shuffles on every reload while the user is reading
        // it. This is **not** `discoveryOrder`: DISC-4's chain also weighs status and first-sighting,
        // and HT-2 asks for count.
        manageRows: [...rows]
          .sort(
            (a, b) =>
              b.cachedCount - a.cachedCount ||
              a.createdAt - b.createdAt ||
              (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
          )
          .map((row) => ({
            id: row.id,
            text: row.text,
            statusOverride: row.statusOverride,
            status: row.status,
            cachedCount: row.cachedCount,
            boardPosition: row.boardPosition,
          })),
        library: await profile.listTemplates(),
      };
    },
    'loadHalftimeContext',
  );
}

/**
 * Narrows `TalkPointRow` to its halftime arm.
 *
 * `listForMatch(id, 'halftime')` already filters, so this never rejects a row in practice — it is
 * how the *type* learns what the query guaranteed, which is what keeps INV-13's discrimination
 * intact all the way to the caller rebuilding a selection.
 */
function isHalftimeTalkPoint(row: TalkPointRow): row is HalftimeTalkPointRow {
  return row.phase === ARTIFACT_PHASE.halftime;
}

/**
 * HT-5 — promote or un-promote, by sending the whole ordered selection.
 *
 * The caller builds the list because §10.2's rank rule requires it: rank *is* the index in what the
 * user sees, so the ordered list is the only lossless way to say what they chose. A
 * `{hypothesisId, promote: boolean}` command would have to reconstruct that order inside a handler
 * that cannot see the screen.
 */
export function commitHalftimeAdjustments(
  matchId: string,
  ordered: readonly TalkPointSelection[],
  onCommitted?: (at: number) => void,
): Promise<DispatchResult> {
  const command: PromoteHalftimeAdjustmentCommand = {
    type: 'PromoteHalftimeAdjustment',
    matchId,
    ordered,
  };
  return dispatch(command, onCommitted ? { onCommitted } : {});
}

/**
 * HT-5 — one entry of the selection, built from the hypothesis it came from.
 *
 * `instructionText` is `null` unless the user opened the pencil (INV-13: *"a halftime adjustment is
 * usable with nothing but its source hypothesis text"*), and `sourceHypothesisId` is always set here
 * because every halftime promotion has a source. `rank` is deliberately absent from the type.
 */
export function newHalftimeAdjustment(
  matchId: string,
  hypothesisId: string,
  instructionText: string | null,
): TalkPointSelection {
  return {
    id: newId(),
    matchId,
    phase: ARTIFACT_PHASE.halftime,
    instructionText,
    sourceHypothesisId: hypothesisId,
    attachedSetPieceId: null,
  };
}

/**
 * HT-8 — build the note row, stamping `matchMinute` through SES-10's one total function.
 *
 * `deriveMatchMinute` yields `halfLengthMin` for a `halftime` match (§2.4), which is what §7.4
 * requires — spelled as a call rather than as `match.halfLengthMin` so INV-7's *"`matchMinute` is
 * the only temporal field a domain derivation reads"* keeps its single producer. Writing the field
 * directly would work today and would silently stop tracking SES-8's boundary re-derivation.
 */
export function newHalftimeNote(match: MatchRow, at: number): MatchNoteRow {
  return {
    id: newId(),
    matchId: match.id,
    phase: ARTIFACT_PHASE.halftime,
    text: null,
    audioBlobId: null,
    matchMinute: deriveMatchMinute(match, match.state, at),
    createdAt: at,
    updatedAt: at,
  };
}

// ── Build unit U-13b — HT-2 and PRE-3 at halftime ────────────────────────────

/**
 * LOCK-6 / DD-3 — put `incomingId` on the board, taking `outgoingId` off when the board is full.
 *
 * `outgoingId` is `null` for §6.2's fill-an-empty-slot path, where DD-3 says *"no sheet appears"*.
 * The caller does **not** decide which path is legal — it passes what the user did, and the handler
 * decides against positions it reads inside the transaction. A caller that computed "there is room"
 * from its own render would be one tap out of date on exactly the surface where a wrong answer means
 * a silently displaced tile.
 */
export function swapBoardTile(
  matchId: string,
  incomingId: string,
  outgoingId: string | null,
): Promise<DispatchResult> {
  const command: SwapBoardTileCommand = { type: 'SwapBoardTile', matchId, incomingId, outgoingId };
  return dispatch(command);
}

/**
 * PRE-3 / HT-2 — add one pattern at halftime, list-only.
 *
 * `toHypothesisRow` is U-7's **one** constructor of a hypothesis from a candidate, reused for the
 * reason `addDiscoveryHypothesis` reuses it: a second constructor is a second place that could
 * assign a `boardPosition`. `createdAt` is a parameter because `domain/` may not read a clock and
 * because §6.1's tiebreak needs distinct values across a batch — here every add is one row at one
 * moment, so a single reading is correct.
 */
export function addHalftimeHypothesis(
  matchId: string,
  candidate: PreMatchCandidate,
  createdAt: number = Date.now(),
): Promise<DispatchResult> {
  const command: AddHypothesisAtHalftimeCommand = {
    type: 'AddHypothesisAtHalftime',
    matchId,
    row: toHypothesisRow(matchId, candidate, createdAt),
  };
  return dispatch(command);
}

/** HT-8 — write or edit the note. Insert-vs-update is decided in the transaction, not here. */
export function commitHalftimeNote(
  row: MatchNoteRow,
  onCommitted?: (at: number) => void,
): Promise<DispatchResult> {
  const command: WriteMatchNoteCommand = { type: 'WriteMatchNote', matchId: row.matchId, row };
  return dispatch(command, onCommitted ? { onCommitted } : {});
}

// ── Build unit U-13c — HT-8's voice half ─────────────────────────────────────

/**
 * HT-8 — start (or stop) the halftime note's **voice** recording.
 *
 * ── Why the note row is built here and not in the recorder ──────────────────
 *
 * `newHalftimeNote` is this module's one constructor of a halftime note, and it is where SES-10's
 * minute is derived through `deriveMatchMinute` rather than read off `match.halfLengthMin`. A second
 * constructor inside `app/recorder.ts` would be a second place for that to drift — and it would put
 * halftime's shape inside a service that FLAG-1 and AUD-2 also use. So the recorder mints the blob
 * id and hands it back through this builder, which is the only piece it cannot supply.
 *
 * ── The draft rides along, so *Record* never discards typed text ─────────────
 *
 * HT-8 is *"text, voice, or both"*, and the user reaching for the record button after typing has not
 * asked to lose what they typed. The uncommitted draft is folded into the same commit that opens the
 * blob row, so *"both"* is reached by **one** transaction rather than by two that could interleave —
 * and `handleStartMatchNoteAudio` normalises the text through the same planner *Done* uses, so `''`
 * and `null` cannot come to mean different things on the two paths.
 *
 * ── Identity is captured at the start, not re-derived at finalize ────────────
 *
 * `existing?.id ?? newId()` fixes **which row** this recording belongs to at the moment the user
 * pressed record (`capture-spawning-instance-handle-at-creation`). Nothing later re-reads "the
 * current halftime note": a reload during the recording cannot repoint the blob at a different row,
 * because the id is already on the committed row and on the live handle.
 *
 * @param existing  the committed note, if the break already has one.
 * @param draftText the uncommitted text in the sheet, or `null` when nothing is pending.
 */
export function startHalftimeNoteAudio(
  match: MatchRow,
  existing: MatchNoteRow | undefined,
  draftText: string | null,
): Promise<RecorderAction> {
  const noteId = existing?.id ?? newId();
  return recorder.startMatchNoteAudio(match, (audioBlobId, at) => {
    const base = existing ?? { ...newHalftimeNote(match, at), id: noteId };
    return {
      ...base,
      // The draft wins when there is one; otherwise whatever is already stored stands. `undefined`
      // and `null` are different answers here — `null` is "the user cleared it".
      text: draftText ?? base.text,
      audioBlobId,
      updatedAt: at,
    };
  });
}
